import { gsap } from "gsap";
import * as PIXI from "pixi.js";

import { AbstractGameMode } from "../../../abstracts/flow/AbstractGameMode";
import { AbstractSpine } from "../../../abstracts/graphics/AbstractSpine";
import { AbstractView } from "../../../abstracts/modules/AbstractView";
import { BackgroundNames } from "../../../abstracts/types/enums";
import { ColorSet, log } from "../../../abstracts/utils/AbstractConsole";
import { getRandomBetween } from "../../../abstracts/utils/JSUtils";
import { displayConfig } from "../../UCConfig";
import { UCSpineNames } from "../../assetsLoader/UCAssetsNames";

export class UCBackgroundView extends AbstractView {
    private normalBackground: PIXI.Container;
    private freespinBackground: PIXI.Sprite;
    private superFreespinBackground: PIXI.Sprite;
    private normalBgMask: PIXI.Graphics;
    private bubblesSpine: AbstractSpine;
    private fishesSpine: AbstractSpine;

    private isIdleActive: boolean = true;
    private idleTween: gsap.core.Tween;

    // private fishesIdleTween: gsap.core.Tween;

    constructor() {
        super(`UCBackgroundView`);
    }

    private swapBackgroundTo(newBackground: PIXI.Sprite | PIXI.Container) {
        newBackground.position.set(-480);

        this.addChildAt(newBackground, 0);
        if (this.children.length > 1) {
            this.removeChildren(1);
        }

        log(`WhackedBackgroundView set ` + this.gameMode.toString(), ColorSet.BLACK);
    }

    private createNormalBackground(): PIXI.Container {
        const backgroundContainer = new PIXI.Container();
        backgroundContainer.name = BackgroundNames.NORMAL;

        const bgImage = new PIXI.Sprite(PIXI.Texture.from(BackgroundNames.NORMAL));
        backgroundContainer.addChild(bgImage);

        this.bubblesSpine = new AbstractSpine(UCSpineNames.BACKGROUND_BUBBLES);
        this.bubblesSpine.position.set(400, 500);
        this.bubblesSpine.getSpineTween(`bubbles_loop`, true);

        this.bubblesSpine.getSpineTween(`bubbles_loop`, true, () => {
            this.addChild(this.bubblesSpine);
        });

        this.fishesSpine = new AbstractSpine(UCSpineNames.BACKGROUND_FISHES);
        this.fishesSpine.position.set(420, 520);
        //
        // let nextFishCounter = 1;
        // const playNextFishSpine = () => {
        //     this.fishesSpine.getSpineTween(`bg_fish${nextFishCounter}`, false, null, null, () => {
        //         nextFishCounter++;
        //         if (nextFishCounter > 2) {
        //             nextFishCounter = 1;
        //         }
        //         playNextFishSpine();
        //     });
        // };
        // playNextFishSpine();

        // backgroundContainer.addChild(this.fishesSpine);

        return backgroundContainer;
    }

    /*private animateNormalBackgroundClouds(cloud: PIXI.Sprite, cloudsLayer: PIXI.Container[]) {
        const layer: PIXI.Container = getRandomElement(cloudsLayer);

        gsap.to(cloud, {
            duration: getRandomBetween(200, 800),
            x: -280 - cloud.width,
            onComplete: () => {
                cloud.x = 1100;
                cloud.y = getRandomBetween(200, 700 - cloud.height); //! check on mobile
                this.animateNormalBackgroundClouds(cloud, cloudsLayer);
            },
        });
        layer.addChild(cloud);
    }*/

    protected initGraphics(): void {
        this.normalBackground = this.createNormalBackground();

        this.freespinBackground = new PIXI.Sprite(PIXI.Texture.from(BackgroundNames.FREESPIN));
        this.superFreespinBackground = new PIXI.Sprite(
            PIXI.Texture.from(BackgroundNames.SUPER_FREESPIN)
        );

        this.normalBgMask = new PIXI.Graphics();
        this.normalBgMask.name = `normalBgMask`;
        this.normalBgMask.beginFill(0xff9900); // to do - revert alpha to 1
        this.normalBgMask.drawRect(
            -480,
            -480,
            displayConfig.backgroundWidth,
            displayConfig.backgroundHeight
        );
        this.normalBgMask.endFill();

        this.addChild(this.normalBgMask);
        this.normalBackground.mask = this.normalBgMask;
    }

    protected onGameModeUpdate() {
        super.onGameModeUpdate();

        switch (this.gameMode) {
            case AbstractGameMode.FREESPIN:
                this.removeChild(this.normalBgMask);
                this.swapBackgroundTo(this.freespinBackground);
                break;

            case AbstractGameMode.SUPER_FREESPIN:
                this.removeChild(this.normalBgMask);
                this.swapBackgroundTo(this.superFreespinBackground);
                break;

            default:
                this.swapBackgroundTo(this.normalBackground);
                this.addChild(this.normalBgMask);

                break;
        }
        this.addChild(this.bubblesSpine);
    }

    public enableIdle() {
        this.isIdleActive = true;
        this.idleTween = gsap.delayedCall(14, this.onIdleRepeat.bind(this));
        // this.fishesIdleTween = gsap.delayedCall(6, this.onFishesIdleRepeat.bind(this));
    }

    public disableIdle() {
        this.isIdleActive = false;
        this.idleTween?.kill();
        // this.fishesIdleTween?.kill();
    }

    private onIdleRepeat() {
        if (this.isIdleActive) {
            // const onSpineComplete = () => {};

            const tl = gsap.timeline({
                onStart: () => {
                    // this.bubblesSpine.alpha = 0;
                    this.fishesSpine.alpha = 0;
                },
                onComplete: () => {
                    // this.bubblesSpine.reset();
                    // this.removeChild(this.bubblesSpine);

                    this.fishesSpine.reset();
                    this.removeChild(this.fishesSpine);

                    if (this.isIdleActive) {
                        this.idleTween?.kill();
                        this.idleTween = gsap.delayedCall(
                            getRandomBetween(14, 20, true),
                            this.onIdleRepeat.bind(this)
                        );
                    }
                },
            });

            // // bubbleAnimation.timeScale(0.1);
            // tl.add(bubbleAnimation, 0);

            // const showBubblesTween = gsap.to(this.bubblesSpine, {
            //     duration: bubbleAnimation.totalDuration() * 0.5,
            //     alpha: 1,
            // });
            // tl.add(showBubblesTween, 0);

            const fishesAnimation = this.fishesSpine.getSpineTween(
                `bg_fish${getRandomBetween(1, 2, true)}`,
                false,
                () => {
                    this.addChild(this.fishesSpine);
                }
            );
            tl.add(fishesAnimation, 0);

            const showFishesTween = gsap.to(this.fishesSpine, {
                duration: fishesAnimation.totalDuration() * 0.5,
                alpha: 0.4,
            });
            tl.add(showFishesTween, 0);

            const hideFishesTween = gsap.to(this.fishesSpine, {
                duration: fishesAnimation.totalDuration() * 0.5,
                alpha: 0,
            });
            tl.add(hideFishesTween, fishesAnimation.totalDuration() * 0.5);

            // const hideBubblesTween = gsap.to(this.bubblesSpine, {
            //     duration: bubbleAnimation.totalDuration() * 0.5,
            //     alpha: 0,
            // });
            // tl.add(hideBubblesTween, fishesAnimation.totalDuration() * 0.5);
            // tl.timeScale(0.1);
        }
    }

    /*private onFishesIdleRepeat() {
        if (this.isIdleActive) {
            const onSpineComplete = () => {
                this.fishesSpine.reset();
                this.removeChild(this.fishesSpine);

                if (this.isIdleActive) {
                    this.fishesIdleTween = gsap.delayedCall(
                        getRandomBetween(5, 18, true),
                        this.onFishesIdleRepeat.bind(this)
                    );
                }
            };
            this.fishesSpine.getSpineTween(
                `bg_fish${getRandomBetween(1, 2, true)}`,
                false,
                () => {
                    this.addChild(this.fishesSpine);
                },
                null,
                onSpineComplete
            );
        }
    }*/

    public showNextBackground(newGameMode: AbstractGameMode) {
        this.gameMode = newGameMode;

        switch (newGameMode) {
            case AbstractGameMode.FREESPIN:
                this.removeChild(this.normalBgMask);
                this.swapBackgroundTo(this.freespinBackground);
                break;

            default:
                this.swapBackgroundTo(this.normalBackground);
                this.addChild(this.normalBgMask);

                break;
        }
    }

    public onOrientationChanged() {
        //handle
    }
}
